// Copyright 2025, University of Maryland, All Rights Reserved
import { v4 as uuidv4 } from 'uuid'
import type { JSONSchema, JSONSchemaProperty } from '@/api/types/schema'
import type { SchemaBuilderSection } from '@/types/schema-builder'
import type { SchemaField, SchemaFieldType, SchemaFieldConfig } from '@/types/schema-fields'
import { getFieldTypeConfig } from '@/utils/schema/schema-type-utils'

/**
 * Extracts schema sections from a JSON schema for editing
 */
export function extractSectionsFromSchema(schema: unknown): SchemaBuilderSection[] {
  if (!schema || typeof schema !== 'object') {
    return []
  }
  
  try {
    const parsedSchema = schema as JSONSchema
    
    if (!parsedSchema.properties) {
      return []
    }
    
    const sections: SchemaBuilderSection[] = []
    
    // Process each section
    for (const [sectionId, sectionSchema] of Object.entries(parsedSchema.properties)) {
      if (!sectionSchema.properties) continue
      
      const properties = sectionSchema.properties
      
      // Extract UI metadata from schema title and const values
      const sectionTitle = sectionSchema.title || sectionId
      const icon = properties.icon?.const ? String(properties.icon.const) : undefined
      const order = Number((sectionSchema as any).order || 1)
      
      // Extract fields
      const fields: SchemaField[] = []
      
      for (const [propKey, propSchema] of Object.entries(properties)) {
        if (propKey === 'icon' || propKey === 'order') continue
        
        const field_type = detectFieldType(propSchema)
        const config = getFieldTypeConfig(field_type)
        
        // Extract field order from metadata (fallback to field position if not found)
        let field_order = fields.length // Default to current position
        if ((propSchema as any).order !== undefined) {
          field_order = (propSchema as any).order
        }
        
        // Customize config based on schema property
        if (field_type === 'integer') {
          config.min = (propSchema as any).minimum ?? 1
          config.max = (propSchema as any).maximum ?? 10
        } else if (field_type === 'enum_field') {
          config.enum_options = (propSchema as any).enum || []
        } else if (field_type === 'score') {
          config.score_min = (propSchema as any).properties?.score?.minimum ?? 0
          config.score_max = (propSchema as any).properties?.score?.maximum ?? 10
          config.type_options = (propSchema as any).properties?.score_type?.enum || ['positive', 'neutral', 'negative']
        } else if (field_type === 'object_array') {
          // Extract properties from object array items
          const items_properties = (propSchema as any).items?.properties
          
          if (items_properties) {
            const extracted_properties: Record<string, SchemaFieldConfig> = {}
            
            for (const [prop_key, prop_schema] of Object.entries(items_properties)) {
              const prop_type = detectFieldType(prop_schema as JSONSchemaProperty)
              const base_config = getFieldTypeConfig(prop_type)
              
              // Customize property config based on its schema
              if (prop_type === 'integer') {
                base_config.min = (prop_schema as any).minimum ?? 0
                base_config.max = (prop_schema as any).maximum ?? 1000
              } else if (prop_type === 'enum_field') {
                base_config.enum_options = (prop_schema as any).enum || []
              } else if (prop_type === 'score') {
                base_config.score_min = (prop_schema as any).properties?.score?.minimum ?? 0
                base_config.score_max = (prop_schema as any).properties?.score?.maximum ?? 10
                base_config.type_options = (prop_schema as any).properties?.score_type?.enum || ['positive', 'neutral', 'negative']
              }
              
              // Extract title and description from the schema property
              if ((prop_schema as any).title) {
                base_config.title = (prop_schema as any).title
              }
              
              if ((prop_schema as any).description) {
                base_config.description = (prop_schema as any).description
              }
              
              extracted_properties[prop_key] = base_config
            }
            
            config.properties = extracted_properties
          }
        }
        
        // Extract title from direct property or generate from field id
        let field_title: string | undefined
        if ((propSchema as any).title) {
          // Direct title property
          field_title = (propSchema as any).title
        } else {
          field_title = propKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }
        
        fields.push({
          uuid: uuidv4(),
          id: propKey,
          title: field_title,
          description: (propSchema as any).description,
          field_type,
          config,
          order: field_order
        })
      }
      
      // Sort fields by order
      fields.sort((a, b) => a.order - b.order)
      
      sections.push({
        uuid: uuidv4(),
        id: sectionId,
        title: sectionTitle,
        icon,
        order,
        fields
      })
    }
    
    return sections.sort((a, b) => a.order - b.order)
  } catch (error) {
    console.error('Error extracting sections from schema:', error)
    return []
  }
}

/**
 * Auto-detect field type from JSON schema structure
 */
export function detectFieldType(schema_property: JSONSchemaProperty): SchemaFieldType {
  if (schema_property.type === 'string' && schema_property.enum) {
    return 'enum_field'
  }

  if (schema_property.type === 'string') {
    return 'string'
  }

  if (schema_property.type === 'integer') {
    return 'integer'
  }

  if (schema_property.type === 'object') {
    const properties = schema_property.properties
    if (properties) {
      // Check if it's a score object with score and score_type properties
      if (properties.score?.type === 'integer' && 
          properties.score_type?.type === 'string' && 
          properties.score_type?.enum) {
        return 'score'
      }
    }
  }

  if (schema_property.type === 'array') {
    if (schema_property.items?.type === 'string') {
      return 'simple_array'
    }
    if (schema_property.items?.type === 'object') {
      return 'object_array'
    }
  }
  
  // Default fallback
  return 'string'
}
